import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Building2, ClipboardCheck, ClipboardList, FileClock, FileSpreadsheet, Flag, GraduationCap, Home, LayoutGrid, Mail, ScanLine, School, Settings, UserPlus, Users, Wallet } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, useSpaceName } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { HomeRedirect, RequireAuth } from "@/components/layout/Guards";
import LoginPage from "@/pages/LoginPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import NotFoundPage from "@/pages/NotFoundPage";
import PublicSchoolPage from "@/pages/PublicSchoolPage";
import KartuPublik from "@/pages/KartuPublik";
import PlatformSummary from "@/pages/platform/PlatformSummary";
import SchoolsList from "@/pages/platform/SchoolsList";
import SchoolCreate from "@/pages/platform/SchoolCreate";
import SchoolDetail from "@/pages/platform/SchoolDetail";
import IdentityReviews from "@/pages/platform/IdentityReviews";
import PlatformAudit from "@/pages/platform/PlatformAudit";
import PlatformBilling from "@/pages/platform/PlatformBilling";
import PlatformPengajuan from "@/pages/platform/PlatformPengajuan";
import PlatformSettings from "@/pages/platform/PlatformSettings";
import PlatformModeration from "@/pages/platform/PlatformModeration";
import MitraHome from "@/pages/mitra/MitraHome";
import MitraPengaturan from "@/pages/mitra/MitraPengaturan";
import MitraPelanggan from "@/pages/mitra/MitraPelanggan";
import MitraPelangganDetail from "@/pages/mitra/MitraPelangganDetail";
import MitraPembayaran from "@/pages/mitra/MitraPembayaran";
import MitraDaftar from "@/pages/mitra/MitraDaftar";
import SchoolHome from "@/pages/school/SchoolHome";
import SchoolProfile from "@/pages/school/SchoolProfile";
import SchoolMembers from "@/pages/school/SchoolMembers";
import SchoolSettings from "@/pages/school/SchoolSettings";
import SchoolInvitations from "@/pages/school/SchoolInvitations";
import SchoolAudit from "@/pages/school/SchoolAudit";
import LanggananPage from "@/pages/school/LanggananPage";
import AcademicPage from "@/pages/school/academic/AcademicPage";
import AttendancePage from "@/pages/school/attendance/AttendancePage";
import GradesPage from "@/pages/school/grades/GradesPage";

const PLATFORM_NAV = [
  { to: "/pusat-kendali", end: true, label: "Ringkasan", icon: LayoutGrid, testId: "nav-platform-summary" },
  { to: "/pusat-kendali/sekolah", label: "Sekolah", icon: Building2, testId: "nav-platform-schools" },
  { to: "/pusat-kendali/langganan", label: "Langganan", icon: Wallet, testId: "nav-platform-billing" },
  { to: "/pusat-kendali/pengajuan", label: "Pengajuan", icon: ClipboardList, testId: "nav-platform-pengajuan" },
  { to: "/pusat-kendali/tinjauan-identitas", label: "Tinjauan Identitas", icon: ClipboardCheck, testId: "nav-platform-reviews" },
  { to: "/pusat-kendali/moderasi-feed", label: "Moderasi Feed", icon: Flag, testId: "nav-platform-moderation" },
  { to: "/pusat-kendali/audit", label: "Log Audit", icon: FileClock, testId: "nav-platform-audit" },
  { to: "/pusat-kendali/pengaturan", label: "Pengaturan", icon: Settings, testId: "nav-platform-settings" },
];

const SCHOOL_NAV = [
  { to: "/sekolah", end: true, label: "Beranda", icon: Home, testId: "nav-school-home", perms: ["profile:read"] },
  { to: "/sekolah/langganan", label: "Langganan", icon: Wallet, testId: "nav-school-langganan", perms: ["billing:read"] },
  { to: "/sekolah/profil", label: "Profil Sekolah", icon: School, testId: "nav-school-profile", perms: ["profile:read"] },
  { to: "/sekolah/anggota", label: "Anggota", icon: Users, testId: "nav-school-members", perms: ["members:read"] },
  { to: "/sekolah/akademik", label: "Data Akademik", icon: GraduationCap, testId: "nav-school-academic", perms: ["academic:read"] },
  // Penilaian dibuka lewat izin grades:* bagi pengurus/guru; siswa dan orang tua masuk lewat peran untuk melihat rapor terbit.
  { to: "/sekolah/penilaian", label: "Penilaian & Rapor", icon: FileSpreadsheet, testId: "nav-school-grades", perms: ["grades:read", "grades:write", "grades:publish", "grades:settings"], selfRoles: ["siswa", "orang_tua"] },
  // Presensi dibuka untuk semua peran: pengurus memakai izin attendance:*, siswa/orang tua melihat rekap dirinya.
  { to: "/sekolah/presensi", label: "Presensi", icon: ScanLine, testId: "nav-school-attendance", perms: ["attendance:read", "attendance:take", "attendance:scan"], selfRoles: ["siswa", "orang_tua", "pegawai_non_asn"] },
  { to: "/sekolah/undangan", label: "Undangan", icon: Mail, testId: "nav-school-invitations", perms: ["invitations:read"] },
  // Log Audit sengaja tidak ditampilkan untuk akun sekolah; hanya Admin Platform dan akun reseller yang memakainya.
  { to: "/sekolah/pengaturan", label: "Pengaturan", icon: Settings, testId: "nav-school-settings" },
];

const MITRA_NAV = [
  { to: "/mitra", end: true, label: "Beranda", icon: LayoutGrid, testId: "nav-mitra-home" },
  { to: "/mitra/pelanggan", label: "Kelola Pelanggan", icon: Users, testId: "nav-mitra-pelanggan" },
  { to: "/mitra/pembayaran", label: "Kelola Pembayaran", icon: Wallet, testId: "nav-mitra-pembayaran" },
  { to: "/mitra/daftar", label: "Daftar Sekolah", icon: UserPlus, testId: "nav-mitra-daftar" },
  { to: "/mitra/pengaturan", label: "Pengaturan", icon: Settings, testId: "nav-mitra-settings" },
];

const MitraLayout = () => (
  <AppShell variant="mitra" spaceLabel="Ruang Mitra" nav={MITRA_NAV} />
);

const SchoolLayout = () => {
  const { me } = useAuth();
  const space = useSpaceName();
  const location = useLocation();
  const isAdmin = me.active_school?.role === "school_admin";
  const billingLocked = me.school_billing_locked === true;

  let nav = SCHOOL_NAV.filter((n) => !n.perms || n.perms.some((p) => me.school_permissions.includes(p)) || n.selfRoles?.includes(me.active_school?.role));

  nav = nav.map((n) => (n.testId === "nav-school-langganan" && !isAdmin ? { ...n, to: "/sekolah/langganan/saya" } : n));

  // Tagihan lewat jatuh tempo: kunci semua menu selain Langganan dan Pengaturan (agar sandi tetap bisa diubah).
  if (billingLocked) nav = nav.filter((n) => ["nav-school-langganan", "nav-school-settings"].includes(n.testId));

  // Akun terkunci langsung diarahkan ke Langganan, termasuk saat membuka Beranda atau menekan F5.
  if (billingLocked && !location.pathname.startsWith("/sekolah/langganan") && location.pathname !== "/sekolah/pengaturan") {
    return <Navigate to={isAdmin ? "/sekolah/langganan" : "/sekolah/langganan/saya"} replace />;
  }

  return <AppShell variant="school" spaceLabel={space} nav={nav} />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/masuk" element={<LoginPage />} />
          <Route path="/undangan" element={<AcceptInvitePage />} />
          <Route path="/reset-sandi" element={<ResetPasswordPage />} />
          <Route path="/publik/:schoolId" element={<PublicSchoolPage />} />
          <Route path="/kartu/:kunci" element={<KartuPublik />} />
          <Route path="/pusat-kendali" element={<RequireAuth space="platform"><AppShell variant="platform" spaceLabel="Pusat Kendali" nav={PLATFORM_NAV} /></RequireAuth>}>
            <Route index element={<PlatformSummary />} />
            <Route path="sekolah" element={<SchoolsList />} />
            <Route path="sekolah/baru" element={<SchoolCreate />} />
            <Route path="sekolah/:id" element={<SchoolDetail />} />
            <Route path="langganan" element={<PlatformBilling />} />
            <Route path="pengajuan" element={<PlatformPengajuan />} />
            <Route path="tinjauan-identitas" element={<IdentityReviews />} />
            <Route path="moderasi-feed" element={<PlatformModeration />} />
            <Route path="audit" element={<PlatformAudit />} />
            <Route path="pengaturan" element={<PlatformSettings />} />
          </Route>
          <Route path="/sekolah" element={<RequireAuth space="school"><SchoolLayout /></RequireAuth>}>
            <Route index element={<SchoolHome />} />
            <Route path="langganan" element={<LanggananPage />} />
            <Route path="langganan/saya" element={<LanggananPage />} />
            <Route path="profil" element={<SchoolProfile />} />
            <Route path="anggota" element={<SchoolMembers />} />
            <Route path="pengaturan" element={<SchoolSettings />} />
            <Route path="undangan" element={<SchoolInvitations />} />
            <Route path="audit" element={<SchoolAudit />} />
            <Route path="akademik" element={<AcademicPage />} />
            <Route path="penilaian" element={<GradesPage />} />
            <Route path="presensi" element={<AttendancePage />} />
          </Route>
          <Route path="/mitra" element={<RequireAuth space="reseller"><MitraLayout /></RequireAuth>}>
            <Route index element={<MitraHome />} />
            <Route path="pelanggan" element={<MitraPelanggan />} />
            <Route path="pelanggan/:id" element={<MitraPelangganDetail />} />
            <Route path="pembayaran" element={<MitraPembayaran />} />
            <Route path="daftar" element={<MitraDaftar />} />
            <Route path="pengaturan" element={<MitraPengaturan />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton containerAriaLabel="Notifikasi" toastOptions={{ closeButtonAriaLabel: "Tutup notifikasi" }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
